import produce, { Draft } from 'immer';
import memoizerific from 'memoizerific';

export function parseIntOrDefault<TDefault>(num: string | nundefined, def: TDefault): number | TDefault {
	if (!num)
		return def;
	
	const parsed = parseInt(num);
	return isNaN(parsed) ? def : parsed;
}

export function getFileExtension(filename: string | nundefined): string | null {
	if (!filename)
		return null;
	
	const idx = filename.lastIndexOf('.');
	if (idx === -1)
		return null;
	
	const ext = filename.slice(idx + 1).trim();
	return !ext ? null : ext;
}

export function filterByType<TResult, TArray>(arr: ReadonlyArray<TArray> | nundefined, getIfType: (item: TArray) => TResult | nundefined): ReadonlyArray<TResult> {
	const result: TResult[] = [];
	
	(arr ?? []).forEach(item => {
		const ifType = getIfType(item);
		if (ifType != null)
			result.push(ifType);
	});
	
	return result;
}

export function findByType<TResult, TArray>(arr: ReadonlyArray<TArray> | nundefined, getIfType: (item: TArray) => TResult | nundefined): TResult | undefined {
	if (!arr)
		return undefined;
	
	for (let item of arr) {
		const ifType = getIfType(item);
		if (ifType != null)
			return ifType;
	}
}

export function isSupportedAudioFileType(type: string): boolean {
	switch (type) {
		case 'audio/wave':
		case 'audio/wav':
		case 'audio/x-wav':
		case 'audio/x-pn-wav':
		case 'audio/mpeg':
			return true;
		default:
			return false;
	}
}

export function assertIsNever(val: never): void {
	// this should never get called. it's here simply
	// as a compiler helper.
	console.log('Error: Encountered unexpected value.');
}

/**
 * a version of produce that allows us to abort or keep
 * changes to the draft. this is nice because sometimes
 * you need to abort part way through an update and you
 * want to make sure that none of the updates made up to
 * that point are applied.
 */
export function conditionalProduce<T>(item: T, updater: (item: Draft<T>) => boolean): T {
    const base: ConditionalProduceBase<T> = {
    	keep: true,
		item
	};
    
    const result = produce<ConditionalProduceBase<T>>(base, b => {
    	b.keep = updater(b.item);
	});
    
    return result.keep ? result.item : item;
}

interface ConditionalProduceBase<T> {
    keep: boolean;
    item: T;
}

export function memoize<T extends Function>(fun: T, cacheSize: number): T {
    return memoizerific(cacheSize)(fun as any);
}

export function logError(msg: string): void {
    if (console.error) {
    	console.error(msg);
	} else {
    	console.log(`Error: ${msg}`);
	}
}

